import { AuthApi } from './api/auth-api';
import { SiretInputComponent } from './components/siret-input';
import { ModalComponent } from './components/modal';
import { AuthView } from './views/auth.view';
import { ApporteurView } from './views/apporteur.view';
import { AdminView } from './views/admin.view';
import { AccountView } from './views/account.view';
import { UserProfile } from './types';
import { escapeHtml } from './utils/formatters';

class App {
  private authView!: AuthView;
  private apporteurView!: ApporteurView;
  private adminView!: AdminView;
  private accountView!: AccountView;
  private currentUser: UserProfile | null = null;

  public init(): void {
    this.initSiretFormatters();
    this.initViews();
    this.initGlobalModalHandlers();
    this.initScrollEffect();
    this.checkSession();
  }

  private initSiretFormatters(): void {
    const regSiret = document.getElementById('regSiret') as HTMLInputElement | null;
    if (regSiret) SiretInputComponent.attach(regSiret, 'siretCounter');

    const prospectSiret = document.getElementById('prospectSiret') as HTMLInputElement | null;
    if (prospectSiret) SiretInputComponent.attach(prospectSiret, 'prospectSiretCounter');

    const prospectDate = document.getElementById('prospectDate') as HTMLInputElement | null;
    if (prospectDate) {
      prospectDate.value = new Date().toISOString().split('T')[0];
    }
  }

  private initViews(): void {
    this.authView = new AuthView((user) => this.onLoginSuccess(user));
    this.apporteurView = new ApporteurView();
    this.adminView = new AdminView();
    this.accountView = new AccountView((user) => {
      this.currentUser = user;
      this.renderHeader();
    });
  }

  private initScrollEffect(): void {
    const header = document.getElementById('mainHeader');
    if (!header) return;
    const onScroll = () => {
      if (window.scrollY > 16) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  private initGlobalModalHandlers(): void {
    // All modal close buttons
    document.querySelectorAll('.btn-close-modal').forEach(btn => {
      btn.addEventListener('click', () => {
        ModalComponent.closeRecapModal();
        ModalComponent.closeEmailModal();
        ModalComponent.closeUserAccountModal();
        ModalComponent.closeResetPasswordModal();
        ModalComponent.closeDeleteUserModal();
        ModalComponent.closeMyAccountModal();
      });
    });

    const modalBackdrops = ['recapModal', 'emailModal', 'userAccountModal', 'userPasswordModal', 'userDeleteModal', 'myAccountModal'];
    modalBackdrops.forEach(id => {
      const el = document.getElementById(id);
      el?.addEventListener('click', (e) => {
        if (e.target === el) {
          ModalComponent.closeRecapModal();
          ModalComponent.closeEmailModal();
          ModalComponent.closeUserAccountModal();
          ModalComponent.closeResetPasswordModal();
          ModalComponent.closeDeleteUserModal();
          ModalComponent.closeMyAccountModal();
        }
      });
    });

    // Recap action buttons
    document.getElementById('recapViewProspectsBtn')?.addEventListener('click', () => {
      ModalComponent.closeRecapModal();
      this.apporteurView.switchTab('list');
    });

    document.getElementById('recapAddAnotherBtn')?.addEventListener('click', () => {
      ModalComponent.closeRecapModal();
      const form = document.getElementById('prospectForm') as HTMLFormElement | null;
      if (form) {
        form.reset();
        const dateInput = document.getElementById('prospectDate') as HTMLInputElement | null;
        if (dateInput) dateInput.value = new Date().toISOString().split('T')[0];
        const counter = document.getElementById('prospectSiretCounter');
        if (counter) counter.textContent = '0 / 14';
      }
      this.apporteurView.switchTab('add');
    });

    document.getElementById('emailModalCloseBtn')?.addEventListener('click', () => {
      ModalComponent.closeEmailModal();
    });

    // Password visibility toggles
    document.querySelectorAll('.btn-toggle-pwd').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement?.querySelector('input');
        if (input) {
          input.type = input.type === 'password' ? 'text' : 'password';
        }
      });
    });
  }

  private async checkSession(): Promise<void> {
    try {
      const user = await AuthApi.getMe();
      this.onLoginSuccess(user);
    } catch {
      this.renderUnauthenticated();
    }
  }

  private onLoginSuccess(user: UserProfile): void {
    this.currentUser = user;
    this.renderHeader();

    const authSection = document.getElementById('authSection');
    const apporteurSection = document.getElementById('apporteurSection');
    const adminSection = document.getElementById('adminSection');

    if (authSection) authSection.style.display = 'none';

    if (user.role === 'admin') {
      if (apporteurSection) apporteurSection.style.display = 'none';
      if (adminSection) adminSection.style.display = 'block';
      this.adminView.activate(user);
    } else {
      if (adminSection) adminSection.style.display = 'none';
      if (apporteurSection) apporteurSection.style.display = 'block';
      this.apporteurView.setUser(user);
    }
  }

  private renderHeader(): void {
    const userActions = document.getElementById('headerUserActions');
    if (!userActions) return;

    if (!this.currentUser) {
      userActions.innerHTML = `
      `;
      document.getElementById('headerLoginBtn')?.addEventListener('click', () => {
        this.renderUnauthenticated();
        this.authView.switchTab('login');
      });
      document.getElementById('headerRegisterBtn')?.addEventListener('click', () => {
        this.renderUnauthenticated();
        this.authView.switchTab('register');
      });
      return;
    }

    userActions.innerHTML = `
      <div class="user-profile-badge">
        <div class="user-avatar-initials">${this.currentUser.raison_sociale.substring(0, 2).toUpperCase()}</div>
        <div class="user-info-text">
          <span class="user-name-span">${escapeHtml(this.currentUser.raison_sociale)}</span>
          <span class="user-role-span">${this.currentUser.role === 'admin' ? 'Direction & Arbitrage' : 'Apporteur d\'affaires'}</span>
        </div>
      </div>
      <button class="btn btn-outline btn-sm" id="headerAccountBtn" title="Mon compte" aria-label="Mon compte">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        Mon compte
      </button>
      <button class="btn btn-outline btn-sm" id="headerLogoutBtn" title="Se déconnecter" aria-label="Se déconnecter">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        Déconnexion
      </button>
    `;

    document.getElementById('headerAccountBtn')?.addEventListener('click', () => {
      if (this.currentUser) this.accountView.open(this.currentUser);
    });

    document.getElementById('headerLogoutBtn')?.addEventListener('click', () => {
      AuthApi.logout();
      this.currentUser = null;
      this.renderUnauthenticated();
    });
  }

  private renderUnauthenticated(): void {
    this.currentUser = null;
    this.renderHeader();

    const authSection = document.getElementById('authSection');
    const apporteurSection = document.getElementById('apporteurSection');
    const adminSection = document.getElementById('adminSection');

    if (authSection) authSection.style.display = 'block';
    if (apporteurSection) apporteurSection.style.display = 'none';
    if (adminSection) adminSection.style.display = 'none';
  }

  // Public bridge for HTML events
  public getAuthView(): AuthView { return this.authView; }
  public getApporteurView(): ApporteurView { return this.apporteurView; }
  public getAdminView(): AdminView { return this.adminView; }
}

let appInstance: App;

// Boot application when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  appInstance = new App();
  appInstance.init();

  // Expose global bridge for existing HTML onclick bindings to prevent ReferenceErrors
  const w = window as any;
  w.switchAuthTab = (tab: 'register' | 'login') => appInstance.getAuthView().switchTab(tab);
  w.switchApporteurTab = (tab: 'add' | 'list' | 'mails') => appInstance.getApporteurView().switchTab(tab);
  w.switchAdminTab = (tab: 'prospects' | 'shared' | 'apporteurs') => appInstance.getAdminView().switchTab(tab);
  w.closeRecapModal = () => ModalComponent.closeRecapModal();
  w.closeEmailModal = () => ModalComponent.closeEmailModal();
  w.loadMyProspects = () => appInstance.getApporteurView().loadProspects();
  w.loadMyNotifications = () => appInstance.getApporteurView().loadNotifications();
  w.loadAdminSharedSirets = () => appInstance.getAdminView().loadSharedSirets();
  w.resetProspectForm = () => {
    const form = document.getElementById('prospectForm') as HTMLFormElement | null;
    if (form) {
      form.reset();
      const dateInput = document.getElementById('prospectDate') as HTMLInputElement | null;
      if (dateInput) dateInput.value = new Date().toISOString().split('T')[0];
      const counter = document.getElementById('prospectSiretCounter');
      if (counter) counter.textContent = '0 / 14';
    }
    appInstance.getApporteurView().switchTab('add');
  };
});
